// Reactive auth + tier state for the whole app. This is the ONE place the rest of the app reads
// "who is signed in and what can they unlock". It exposes a plain snapshot { user, session, tier,
// profile, isPremium, isAdmin } and a subscribe() — no framework, same declarative style as the
// rest of src/.
//
// FREEMIUM: signed-out is the default and fully functional. tier defaults to 'free'; nothing here
// gates the chart. Later work reads isPremium / isAdmin to UNLOCK premium tiles + social — it must
// never use the signed-out state to lock the base app.
//
// OFFLINE-FIRST: after a successful sign-in we read the tier from public.accounts and CACHE it
// (keyed by user id) in localStorage, alongside the persisted session (see ./client). On the next
// cold start with no network, the cached session restores and we re-apply the cached tier
// immediately, so premium stays unlocked offline. The network read, when it succeeds, refreshes
// the cache.

import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './client';

export type Tier = 'free' | 'premium' | 'admin';

/** public.profiles row (readable for all authenticated users per RLS). */
export interface Profile {
  username: string | null;
  display_name: string | null;
  boat_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

export interface AuthState {
  user: User | null;
  session: Session | null;
  tier: Tier;
  profile: Profile | null;
  isPremium: boolean; // premium OR admin — the gate later work checks for premium features
  isAdmin: boolean;
}

/** Result of an auth action, shaped for direct display in the login panel. */
export interface AuthResult {
  ok: boolean;
  message?: string;
}

type Listener = (s: AuthState) => void;

const TIER_VALUES: readonly Tier[] = ['free', 'premium', 'admin'];
const isTier = (v: unknown): v is Tier => typeof v === 'string' && (TIER_VALUES as readonly string[]).includes(v);

// ── offline tier/profile cache (keyed by user id so a different user can't inherit a stale tier) ──
const CACHE_KEY = 'seabearing:auth:cache';
interface Cache {
  userId: string;
  tier: Tier;
  profile: Profile | null;
}
function loadCache(): Cache | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Cache;
    if (typeof c?.userId === 'string' && isTier(c.tier)) return c;
  } catch {
    /* unavailable / corrupt — treat as no cache */
  }
  return null;
}
function saveCache(c: Cache): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    /* private mode — premium just won't persist offline; never fatal */
  }
}
function clearCache(): void {
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/** Redirect target for magic-link / OAuth: back to the current page, minus any query/fragment. */
function redirectTo(): string {
  const { origin, pathname } = window.location;
  return origin + pathname;
}

const friendly = (e: { message?: string } | null | undefined): string =>
  e?.message?.trim() || 'Something went wrong. Try again.';

class AuthStore {
  private state: AuthState = { user: null, session: null, tier: 'free', profile: null, isPremium: false, isAdmin: false };
  private listeners = new Set<Listener>();

  // Resolves once the INITIAL session has been resolved from storage (offline-first: no network
  // needed) — i.e. we KNOW whether the returning user is signed in. The auth gate waits on this
  // before deciding wall-vs-app, so it never flashes the login form ahead of a cached session.
  private markReady!: () => void;
  private isReady = false;
  readonly ready: Promise<void> = new Promise<void>((res) => { this.markReady = res; });

  constructor() {
    // The single subscription. supabase fires INITIAL_SESSION on subscribe with the restored
    // session (or null), then SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED / USER_UPDATED as they
    // happen. detectSessionInUrl also lands the post-redirect session through here.
    supabase.auth.onAuthStateChange((_event, session) => {
      this.applySession(session);
      if (!this.isReady) { this.isReady = true; this.markReady(); }
    });
    // Safety net: if the auth client never reports (init error), unblock the gate so the user can
    // at least see the login wall rather than an endless splash.
    setTimeout(() => { if (!this.isReady) { this.isReady = true; this.markReady(); } }, 8000);
  }

  /** Current snapshot. */
  get snapshot(): AuthState {
    return this.state;
  }

  // Convenience reads of the current tier gate (always reflect the latest applied state).
  get isPremium(): boolean { return this.state.isPremium; }
  get isAdmin(): boolean { return this.state.isAdmin; }
  get tier(): Tier { return this.state.tier; }

  /** Subscribe to state changes. Fires immediately with the current state. Returns an unsubscribe. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  // ── auth actions (each returns a display-ready result; nothing throws to the caller) ──

  async signUpWithEmail(email: string, password: string): Promise<AuthResult> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo() },
    });
    if (error) return { ok: false, message: friendly(error) };
    // A signup trigger creates the account+profile server-side. If email confirmation is on,
    // there's no session yet — tell the user to confirm.
    if (!data.session) return { ok: true, message: 'Check your email to confirm your account.' };
    return { ok: true };
  }

  async signInWithEmail(email: string, password: string): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true };
  }

  /** Passwordless OTP / magic-link sign-in. Creates the user if they don't exist. */
  async signInWithMagicLink(email: string): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo() },
    });
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true, message: 'Check your email for the sign-in link.' };
  }

  /**
   * OAuth via Google / Apple. These providers need to be enabled + configured in the Supabase
   * console (the founder's task: client IDs/secrets + this app's redirect URL). Until then the
   * call returns a "provider is not enabled" error, which the login panel surfaces — so the
   * buttons effectively no-op cleanly rather than break.
   */
  async signInWithOAuth(provider: 'google' | 'apple'): Promise<AuthResult> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: redirectTo() },
    });
    // On success the browser navigates to the provider, so we never reach the resolve with data.
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true };
  }

  async signOut(): Promise<AuthResult> {
    const { error } = await supabase.auth.signOut();
    // Drop the cached tier regardless — the onAuthStateChange(SIGNED_OUT) also resets state.
    clearCache();
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true };
  }

  // ── internal ──

  private applySession(session: Session | null): void {
    const user = session?.user ?? null;
    if (!user) {
      // signed out → back to anonymous free.
      this.set({ user: null, session: null, tier: 'free', profile: null });
      return;
    }
    // Restore the cached tier/profile for THIS user immediately (offline-ready), then refresh
    // from the network in the background.
    const cached = loadCache();
    const tier: Tier = cached && cached.userId === user.id ? cached.tier : 'free';
    const profile = cached && cached.userId === user.id ? cached.profile : null;
    this.set({ user, session, tier, profile });
    void this.refreshFromNetwork(user.id);
  }

  /** Read tier from public.accounts + the profile, then update state + offline cache. Best effort:
   *  offline or a transient error just keeps the cached values. */
  private async refreshFromNetwork(userId: string): Promise<void> {
    try {
      const [acct, prof] = await Promise.all([
        supabase.from('accounts').select('tier').eq('id', userId).single(),
        supabase.from('profiles').select('username,display_name,boat_name,avatar_url,bio').eq('id', userId).single(),
      ]);
      // A guard against a late response for a user we've since signed out of / switched away from.
      if (this.state.user?.id !== userId) return;

      const tier: Tier = !acct.error && isTier(acct.data?.tier) ? (acct.data.tier as Tier) : this.state.tier;
      const profile: Profile | null = !prof.error && prof.data ? (prof.data as Profile) : this.state.profile;
      this.set({ tier, profile });
      saveCache({ userId, tier, profile });
    } catch {
      /* offline / network error — keep the cached tier; premium stays as it was */
    }
  }

  private set(patch: Partial<AuthState>): void {
    const next: AuthState = { ...this.state, ...patch };
    next.isAdmin = next.tier === 'admin';
    next.isPremium = next.tier === 'premium' || next.tier === 'admin';
    this.state = next;
    for (const fn of this.listeners) fn(this.state);
  }
}

/** The app-wide auth store. Import this anywhere that needs auth/tier state. */
export const auth = new AuthStore();
